import { useState, useEffect, useRef } from 'react';

import { useAuthentication, isSignedOutGracePeriodActive } from '../../hooks/useAuthentication';
import { supabase } from '../../lib/supabase';
import { useUserProfile } from '../../modules/dashboard/hooks/useUserProfile';
import { isOnboardingComplete } from '../../modules/onboarding/onboardingGate';
import { captureReferralCodeFromUrl, attributeSignupIfPending } from '../../services/referralAttribution';
import { captureOutreachPersonIdFromUrl, attributeOutreachSignupIfPending } from '../../services/outreachAttribution';
import { useCookieConsent } from '../../shared/hooks/useCookieConsent';
import { useUserPreferences } from '../../shared/hooks/useUserPreferences';
import { logEvent } from '../../shared/services/utils/loggingService';
import { appLogger } from '../../utils/appLogger';
import { isNativePlatform } from '../../utils/capacitorUtils';
import { clearUserLocalData } from '../../utils/clearUserData';
import {
  bootstrapPaymentReturn,
  isPaymentBootRoute,
  isPaymentFlowInProgress,
  isPaymentReturnPath,
  readPaymentReturnTarget,
} from '../../utils/paymentReturnBootstrap';
import {
  POST_AUTH_LANDING_PATH,
  POST_AUTH_LANDING_VIEW,
  resolvePostAuthRedirect,
} from '../router/postAuthLanding';
import { enforcePostAuthRouteGuard } from '../router/postAuthRouteGuard';
import { collapseSidebarForPostLogin } from '../../modules/shell/sidebarState';
import {
  AUTH_PATH_PREFIXES,
  appViewToPath,
  isProtectedRoute,
  resolveAppViewFromPath,
} from '../router/routeRegistry';
import { TOUR_NAVIGATE_EVENT } from '../../lib/tourNavigation';
import { navigateToB2BApp } from '../../config/b2bAppUrl';

function isPrerenderBuild(): boolean {
  return typeof window !== 'undefined'
    && !!(window as { __GGJ_PRERENDER__?: boolean }).__GGJ_PRERENDER__;
}

export type AppView = 
  | 'home' 
  | 'login' 
  | 'signup'
  | 'signup-consent'
  | 'optimize'
  | 'onboarding'
  | 'dashboard'
  | 'agent'
  | 'referrals'
  | 'admin-analytics'
  | 'control-panel'
  | 'cron-jobs-admin'
  | 'testimonials-admin'
  | 'translations-admin'
  | 'dev-datatable'
  | 'dev-coachmark'
  | 'profile'
  | 'dashboard-selector'
  | 'forgot-password' 
  | 'reset-password' 
  | 'auth-confirm'
  | 'privacy-policy' 
  | 'cookie-policy' 
  | 'terms-of-service' 
  | 'gdpr-compliance'
  | 'dpdp-compliance'
  | 'trust-center'
  | 'refund-cancellation' 
  | 'faq'
  | 'faqs' 
  | 'about-us' 
  | 'blog'
  | 'blog-post'
  | 'blog-cms'
  | 'blog-editor'
  | 'careers'
  | 'careers-post'
  | 'careers-apply'
  | 'contact-us' 
  | 'email-unsubscribe'
  | 'pricing'
  | 'credits-checkout'
  | 'payment-checkout'
  | 'payment-success'
  | 'payment-failed'
  | 'payment-history'
  | 'debug-logs'
  | 'join-cohort'
  | 'developers'
  | 'business'
  | 'job-board'
  | 'success-stories'
  | 'features-overview'
  | 'problem'
  | 'who-its-for'
  | 'feature-smart-matching'
  | 'feature-resume-optimizer'
  | 'feature-interview-prep'
  | 'resume-check'
  | 'success-story'
  | 'not-found';

interface AppState {
  currentView: AppView;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

export const useAppState = () => {
  const { session, user, loading: authLoading, signOut } = useAuthentication();
  const { consentState } = useCookieConsent();
  const { preferences, loading: preferencesLoading } = useUserPreferences();
  const { profile: userProfile, isLoading: profileLoading } = useUserProfile();
  // SECURITY: Compute initial currentView from the URL synchronously so the
  // first paint already matches the requested path. Previously this defaulted
  // to 'home' which caused the marketing landing page to flash for a frame
  // when a signed-out user typed a protected URL like /dashboard. We do NOT
  // expose any protected page here — protected paths fall back to a neutral
  // value and the spinner stays up (loading=true) until auth resolves and the
  // effect below makes a real security decision.
  const [appState, setAppState] = useState<AppState>(() => {
    if (typeof window === 'undefined') {
      return { currentView: 'home', isAuthenticated: false, loading: true, error: null };
    }
    try {
      const initialPath = window.location.pathname.replace(/\/+$/, '') || '/';
      const initialSearch = window.location.search;
      const initialResolved = resolveAppViewFromPath(initialPath);
      const paymentReturn = isPaymentReturnPath(initialPath, initialSearch);
      const paymentFlowActive = isPaymentFlowInProgress();
      const paymentBoot = isPaymentBootRoute(initialPath);
      // For protected URLs the spinner shows until auth resolves; we set
      // currentView to the resolved view only as a placeholder. AppRouter
      // gates protected views behind isAuthenticated so this can't leak.
      // For public URLs we render the resolved view immediately (no flash).
      // Payment return: render /payment-success immediately (no auth spinner flash).
      // Cashfree return in flight on `/`: never paint marketing home — stay loading
      // until bootstrap redirect or init resolves to payment-success.
      const recoveringPaymentReturn =
        initialPath === '/payment-success'
        || (paymentFlowActive && initialPath === '/')
        || (initialPath === '/' && !!readPaymentReturnTarget());
      const onPaymentSuccessPath = initialPath === '/payment-success';
      const onPaymentFailedPath = initialPath === '/payment-failed';
      return {
        currentView: recoveringPaymentReturn || onPaymentSuccessPath ? 'payment-success' : initialResolved.view,
        isAuthenticated: false,
        loading: paymentReturn || recoveringPaymentReturn || onPaymentSuccessPath || onPaymentFailedPath
          || (paymentBoot && paymentFlowActive)
          ? false
          : true,
        error: null,
      };
    } catch {
      return { currentView: 'home', isAuthenticated: false, loading: true, error: null };
    }
  });

  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  // Prevent duplicate login→agent navigation (GIS fires SIGNED_IN before onSuccess).
  const postAuthLandingRef = useRef<{ path: string; view: AppView } | null>(null);

  const commitPostAuthLanding = (
    targetPath: string,
    targetView: AppView,
    source: string,
  ): boolean => {
    const committed = postAuthLandingRef.current;
    if (
      committed?.path === targetPath &&
      committed?.view === targetView &&
      window.location.pathname === targetPath
    ) {
      appLogger.debug(`POST AUTH: skip duplicate landing from ${source}`);
      return false;
    }
    postAuthLandingRef.current = { path: targetPath, view: targetView };
    if (window.location.pathname !== targetPath) {
      window.history.replaceState({}, '', targetPath);
    }
    setAppState(prev => ({
      ...prev,
      currentView: targetView,
      isAuthenticated: true,
      loading: false,
    }));
    return true;
  };

  // Safety: never leave loading true forever – force clear after 8s so spinner cannot stick
  const LOADING_TIMEOUT_MS = 8000;
  useEffect(() => {
    if (!appState.loading) return;
    const t = setTimeout(() => {
      setAppState((prev) => (prev.loading ? { ...prev, loading: false } : prev));
    }, LOADING_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [appState.loading]);

  // Initialize app state based on URL and authentication
  useEffect(() => {
    // TASK 7-2 — capture `?ref=CODE` from the landing URL into
    // localStorage BEFORE any navigation happens. The captured code is
    // consumed later by `attributeSignupIfPending()` after signup
    // completes (see AuthConfirmPage). Best-effort, never throws.
    // Outreach pid must be captured before referral strips ?ref=
    captureOutreachPersonIdFromUrl();
    captureReferralCodeFromUrl();

    const initializeAppState = async () => {
      try {
        // Puppeteer prerender: skip auth/session guards — getSession() can hang in CI
        // and blocks ggj-prerender-ready (Navigation timeout on Netlify).
        if (isPrerenderBuild()) {
          const prerenderPath = window.location.pathname;
          const resolved = resolveAppViewFromPath(prerenderPath);
          if (resolved.redirectTo && prerenderPath !== resolved.redirectTo) {
            window.history.replaceState({}, '', resolved.redirectTo);
          }
          setAppState((prev) => ({
            ...prev,
            currentView: resolved.view,
            isAuthenticated: false,
            loading: false,
            error: null,
          }));
          return;
        }

        // CRITICAL: Check for sign-out flag FIRST
        // This prevents back button from bypassing sign-out
        const userSignedOut = localStorage.getItem('user_signed_out');
        if (userSignedOut) {
          appLogger.debug('CRITICAL: User signed out - forcing full sign-out');
          
          const currentPath = window.location.pathname;
          const isOnAuthPage = currentPath === '/login' || currentPath === '/signup' || currentPath === '/';
          
          if (!isOnAuthPage) {
            appLogger.debug('CRITICAL: On protected route after sign-out - redirecting');
            await supabase.auth.signOut();
            clearUserLocalData();
            sessionStorage.clear();
            // DO NOT clear localStorage here - keep the signed_out flag active
            // It will be cleared only on successful sign-in
            window.location.replace('/login');
            return;
          } else {
            // Already on auth page - ensure session is cleared but allow sign-in
            appLogger.debug('CRITICAL: On auth page - clearing flag');
            await supabase.auth.signOut();
            clearUserLocalData();
            sessionStorage.clear();
            localStorage.removeItem('user_signed_out');
            appLogger.debug('CRITICAL: Cleared signed_out flag');
          }
        }
        
        let path = window.location.pathname;
        let searchParams = new URLSearchParams(window.location.search);
        const urlHash = window.location.hash;
        
        appLogger.debug('AppStateManager: Initializing from path:', path);
        appLogger.debug('AppStateManager: URL hash:', window.location.hash);
        appLogger.debug('AppStateManager: Full URL:', window.location.href);

        // Cashfree return: preserve session before tab-session guard runs (sessionStorage is often cleared).
        if (isPaymentReturnPath(path, searchParams.toString()) || isPaymentFlowInProgress()) {
          bootstrapPaymentReturn();
        }

        // Bootstrap may replaceState `/` → `/payment-success` — re-read URL before routing.
        path = window.location.pathname;
        searchParams = new URLSearchParams(window.location.search);

        // Post-login: block marketing home and auth entry URLs (back/forward bypass).
        const postAuthGuard = enforcePostAuthRouteGuard(!!session);
        if (postAuthGuard) {
          path = postAuthGuard.path;
          appLogger.debug('POST AUTH GUARD: blocked public entry, now at', path);
        }
        
        // Check for OAuth callback with access_token in hash or code in query params
        const hasOAuthCallback = urlHash.includes('access_token') || urlHash.includes('refresh_token') || searchParams.get('code');
        appLogger.debug('AppStateManager: hasOAuthCallback:', hasOAuthCallback);
        
        // CRITICAL: If OAuth callback detected, set tab marker IMMEDIATELY
        // This prevents session invalidation when browser navigates and clears the hash
        if (hasOAuthCallback) {
          appLogger.debug('SESSION: OAuth callback - setting tab marker');
          sessionStorage.setItem('tab_session_active', Date.now().toString());
        }
        
        // CRITICAL: Session invalidation on tab/window close
        // But SKIP this check during OAuth callbacks OR on /auth/confirm page
        // Also SKIP on native (Capacitor/Android): sessionStorage is ALWAYS cleared on
        // every app launch (process is killed), so the tab marker will never survive —
        // we must not sign the user out just because they reopened the app.
        const isAuthConfirmPage = path === '/auth/confirm';
        const skipTabSessionGuard =
          isPaymentReturnPath(path, searchParams.toString()) || isPaymentFlowInProgress();
        if (!hasOAuthCallback && !isAuthConfirmPage && !isNativePlatform() && !skipTabSessionGuard) {
          // When user closes tab/window, sessionStorage is cleared by browser
          // When they open again, sessionStorage will be empty but Supabase session might still exist
          const tabSessionMarker = sessionStorage.getItem('tab_session_active');
          const { data: { session: storageSession } } = await supabase.auth.getSession();
          
          if (storageSession && !tabSessionMarker) {
            // Valid auth in localStorage but no per-tab marker — this happens on
            // new tabs, Vite HMR reloads, and duplicate-tab opens. Do NOT sign out;
            // refresh the marker so the protected-route guard stays satisfied.
            appLogger.debug('SESSION: Restoring tab marker for persisted auth session');
            sessionStorage.setItem('tab_session_active', Date.now().toString());
          } else if (storageSession) {
            // Normal operation - session is valid, set/refresh the tab marker so next navigation (e.g. payment-success → dashboard) does not invalidate
            appLogger.debug('SESSION: Tab session active - setting/refreshing marker');
            sessionStorage.setItem('tab_session_active', Date.now().toString());
          }
          
          // Also set marker when session becomes available from hook
          if (session) {
            sessionStorage.setItem('tab_session_active', Date.now().toString());
          }
        }
        
        // Check for password reset tokens (ONLY in query params, NOT hash)
        const hasRecoveryParams = searchParams.get('type') === 'recovery' && 
                                 searchParams.get('token_hash');
        appLogger.debug('AppStateManager: hasRecoveryParams:', hasRecoveryParams);
        
        // CRITICAL: Handle OAuth redirects IMMEDIATELY before showing any UI
        if (hasOAuthCallback && session) {
          appLogger.debug('AppStateManager: OAuth callback with session - redirect to agent');
          
          // Clear any password reset flags
          sessionStorage.removeItem('mandatory_password_reset');
          sessionStorage.removeItem('password_reset_in_progress');
          sessionStorage.removeItem('recovery_token_hash');
          sessionStorage.removeItem('recovery_session_id');
          sessionStorage.removeItem('reset_token_hash');
          sessionStorage.removeItem('reset_target_email');

          // TASK 7-2 — attribute referral for OAuth signups intercepted by this
          // fast-path (e.g. LinkedIn redirect that already has a session set).
          // Only fires when the user explicitly chose Sign Up and there is a
          // pending referral code; the RPC is idempotent so duplicate calls are safe.
          const oauthIntendedAction = localStorage.getItem('oauth_intended_action');
          if (oauthIntendedAction === 'signup') {
            try { await attributeSignupIfPending(); } catch { /* best-effort */ }
            try { await attributeOutreachSignupIfPending(); } catch { /* best-effort */ }
          }
          
          collapseSidebarForPostLogin();
          window.history.replaceState({}, '', '/agent');
          
          setAppState(prev => ({
            ...prev,
            currentView: 'agent',
            isAuthenticated: true,
            loading: false
          }));
          return;
        }
        
        // If OAuth callback detected on home page, redirect to /auth/confirm
        if (hasOAuthCallback && path === '/' && !session) {
          appLogger.debug('AppStateManager: OAuth on home - redirect to /auth/confirm');
          window.history.replaceState({}, '', '/auth/confirm' + window.location.hash);
          setAppState(prev => ({
            ...prev,
            currentView: 'auth-confirm',
            loading: false
          }));
          return;
        }
        
        // IMPORTANT: Only show auth-confirm page for password reset flows OR OAuth callbacks
        // OAuth: Check if we're on /auth/confirm path or have OAuth tokens in hash
        // Password Reset: Check for recovery params in query string
        const isPasswordReset = hasRecoveryParams;
        const isOAuthFlow = hasOAuthCallback || path === '/auth/confirm';
        const needsAuthConfirm = isPasswordReset || (isOAuthFlow && path === '/auth/confirm');
        
        if (needsAuthConfirm) {
          appLogger.debug('AppStateManager: Auth confirm page needed', { 
            isOAuthFlow, 
            isPasswordReset,
            hasSession: !!session 
          });
          setAppState(prev => ({
            ...prev,
            currentView: 'auth-confirm',
            isAuthenticated: !!session,
            loading: false
          }));
          return;
        }
        
        // For OAuth callback without session yet, show loading and let AuthConfirmPage handle it silently
        if (hasOAuthCallback && !session) {
          appLogger.debug('AppStateManager: OAuth without session - processing');
          setAppState(prev => ({
            ...prev,
            currentView: 'auth-confirm',
            isAuthenticated: false,
            loading: false
          }));
          return;
        }
        
        // If we're on /auth/confirm with a session but no valid params, redirect to agent
        if (path === '/auth/confirm' && session && !isPasswordReset && !hasOAuthCallback) {
          appLogger.debug('AppStateManager: Invalid auth-confirm - redirect to agent');
          window.history.replaceState({}, '', '/agent');
          setAppState(prev => ({
            ...prev,
            currentView: 'agent',
            isAuthenticated: true,
            loading: false
          }));
          return;
        }

        // SECURITY: Resolve the URL once via the canonical route registry —
        // this fixes the prior drift where /account-settings, /my-activities,
        // /admin/jobs, etc. were missing from the inline lists and let an
        // unauthenticated user open those pages.
        const resolved = resolveAppViewFromPath(path);
        const pathIsProtected = resolved.protected;

        // ─────────────────────────────────────────────────────────────────
        // FLASH FIX: Defer the final setAppState while auth is still loading
        // on a protected route. Previously the code below fell through to a
        // `setAppState({ loading: false, currentView: 'dashboard' })` even
        // when `authLoading=true && !session`, causing the protected page to
        // render for one frame before the security check (which re-runs once
        // authLoading flips to false) redirected to /login.
        //
        // By bailing out here while authLoading is true on a protected route
        // we keep `loading: true` (the spinner) until useAuthentication has
        // a definitive answer. The effect re-fires when authLoading flips
        // (it's in the dep array) and the proper security gate runs then.
        // ─────────────────────────────────────────────────────────────────
        if (pathIsProtected && authLoading && !session) {
          appLogger.debug('SECURITY: Auth still loading on protected route — keeping spinner up');
          return;
        }
        
        // CRITICAL: Only block access if TRULY unauthenticated
        // Don't block during:
        // 1. Initial session loading (authLoading = true)
        // 2. OAuth completion (within 5 second grace period)
        // 3. Normal navigation when already authenticated
        // 4. useAuthentication SIGNED_OUT grace period (cross-tab token rotation)
        if (pathIsProtected && !session && !authLoading) {
          // If useAuthentication is currently in its SIGNED_OUT grace-period polling,
          // wait up to 4 s for recovery before making a security decision.
          if (isSignedOutGracePeriodActive) {
            appLogger.debug('SECURITY: SIGNED_OUT grace period active – deferring security check');
            const waitForGracePeriod = async () => {
              for (let i = 0; i < 8; i++) {
                await new Promise(r => setTimeout(r, 500));
                if (!isSignedOutGracePeriodActive) break;
              }
              // Re-run the initialization logic once the grace period ends
              const { data: { session: recovered } } = await supabase.auth.getSession();
              if (recovered) {
                appLogger.debug('SECURITY: Session recovered after grace period – not redirecting');
                return;
              }
              // Still no session – fall through to security block below
              appLogger.debug('SECURITY: No session after grace period wait');
              sessionStorage.setItem('redirect_after_login', path);
              window.history.replaceState({}, '', '/login');
              setAppState(prev => ({ ...prev, currentView: 'login', isAuthenticated: false, loading: false, error: null }));
            };
            void waitForGracePeriod();
            return;
          }

          // Double-check: Is there actually a session in Supabase storage?
          // This prevents false positives during React re-renders
          const checkSessionExists = async () => {
            let storageSession = (await supabase.auth.getSession()).data.session;

            // If getSession returns null, try refreshSession once as a last resort
            // (handles cross-tab token rotation consuming the refresh token)
            if (!storageSession) {
              try {
                const { data: { session: refreshed } } = await supabase.auth.refreshSession();
                if (refreshed) {
                  appLogger.debug('SECURITY: Session recovered via refreshSession');
                  storageSession = refreshed;
                }
              } catch (_) { /* ignore */ }
            }
            
            // CRITICAL: Also check for phone auth session
            const phoneAuthSession = localStorage.getItem('phone_auth_session');
            const hasPhoneSession = !!phoneAuthSession;
            
            if (storageSession || hasPhoneSession) {
              appLogger.debug(`SECURITY: ${storageSession ? 'Supabase' : 'Phone'} session in storage`);

              // Use the canonical route registry to resolve the view — this
              // is the same source of truth used by initial-render and
              // popstate handling, so they can no longer drift.
              const resolvedForAuth = resolveAppViewFromPath(path);
              const viewToShow: AppView = resolvedForAuth.notFound
                ? 'agent'              // unknown URL but session present → land on agent (post-auth home)
                : resolvedForAuth.view;

              appLogger.debug(`SECURITY: Setting view ${viewToShow} for path ${path}`);
              sessionStorage.setItem('tab_session_active', Date.now().toString());
              setAppState(prev => ({
                ...prev,
                currentView: viewToShow,
                isAuthenticated: true,
                loading: false
              }));
              return; // Don't block
            }
            
            // Payment return: allow /payment-success when returning from Cashfree (URL params, in-progress flag, or active page marker).
            const paymentPageMarker = (() => { try { return sessionStorage.getItem('ggj_payment_page_active'); } catch { return null; } })();
            const paymentInProgress =
              (path === '/payment-success' || path === '/payment-failed')
              && (
                localStorage.getItem('payment_in_progress')
                || searchParams.has('order_id')
                || searchParams.has('subscription_id')
                || searchParams.has('failed')
                || paymentPageMarker === path
              );
            if (paymentInProgress) {
              sessionStorage.setItem('tab_session_active', Date.now().toString());
              const { data: { session: existingSession } } = await supabase.auth.getSession();
              const paymentView: AppView = path === '/payment-failed' ? 'payment-failed' : 'payment-success';
              setAppState(prev => ({
                ...prev,
                currentView: paymentView,
                isAuthenticated: !!existingSession,
                loading: false,
                error: null
              }));
              return;
            }

            // No session in storage either - now check OAuth grace period
            const oauthJustCompleted = sessionStorage.getItem('oauth_just_completed');
            const oauthCompletedTime = oauthJustCompleted ? parseInt(oauthJustCompleted) : 0;
            const timeSinceOAuth = Date.now() - oauthCompletedTime;
            
            // Give 5 seconds grace period for OAuth session to load
            if (oauthJustCompleted && timeSinceOAuth < 5000) {
              appLogger.debug('SECURITY: OAuth completed, polling for session', timeSinceOAuth, 'ms');
              
              // FIXED: Poll for session in a proper async loop
              const pollForSession = async () => {
                for (let attempt = 1; attempt <= 10; attempt++) {
                  appLogger.debug(`SECURITY: Polling attempt ${attempt}/10`);
                  await new Promise(resolve => setTimeout(resolve, 300));
                  
                  const { data: { session: retrySession } } = await supabase.auth.getSession();
                  
                  if (retrySession) {
                    appLogger.debug('SECURITY: Session found on attempt', attempt);
                    sessionStorage.removeItem('oauth_just_completed');
                    
                    // Session found! Update state and navigate to agent (post-auth landing)
                    setAppState(prev => ({
                      ...prev,
                      currentView: 'agent',
                      isAuthenticated: true,
                      loading: false
                    }));
                    return;
                  }
                }
                
                // After 10 attempts (3 seconds), give up
                appLogger.error('SECURITY: Session not found after 10 attempts - redirect to login');
                sessionStorage.removeItem('oauth_just_completed');
                window.history.replaceState({}, '', '/login');
                setAppState(prev => ({
                  ...prev,
                  currentView: 'login',
                  isAuthenticated: false,
                  loading: false,
                  error: null
                }));
              };
              
              // Start polling (non-blocking)
              pollForSession();
              
              // Show loading state while polling
              setAppState(prev => ({
                ...prev,
                loading: true
              }));
              return;
            }
            
            // Clear the OAuth flag if expired
            if (oauthJustCompleted) {
              appLogger.debug('SECURITY: OAuth grace period expired');
              sessionStorage.removeItem('oauth_just_completed');
            }
            
            appLogger.debug('SECURITY: Blocked unauthenticated access:', path);
            appLogger.debug('SECURITY: Redirecting to /login');
            
            // Store intended destination for redirect after login (never send back to payment-success)
            const redirectTarget = path === '/payment-success' ? '/dashboard' : path;
            sessionStorage.setItem('redirect_after_login', redirectTarget);
            
            window.history.replaceState({}, '', '/login');
            setAppState(prev => ({
              ...prev,
              currentView: 'login',
              isAuthenticated: false,
              loading: false,
              error: null
            }));
          };
          
          // Call the async check
          checkSessionExists();
          return;
        }
        
        // ── Native (Capacitor/Android) override ──────────────────────────────
        // On native ALL URL-based routing is bypassed. The WebView always
        // starts at https://localhost/ but after file-picker / OAuth returns
        // the activity may be recreated with a pushState URL like /optimize/upload.
        // In all cases, route native users straight to dashboard or login.
        if (isNativePlatform()) {
          const { data: { session: nativeSession } } = await supabase.auth.getSession();
          if (nativeSession) {
            appLogger.debug('Native: session found → agent');
            sessionStorage.setItem('tab_session_active', Date.now().toString());
            window.history.replaceState({}, '', '/agent');
            setAppState(prev => ({
              ...prev,
              currentView: 'agent',
              isAuthenticated: true,
              loading: false,
            }));
          } else {
            appLogger.debug('Native: no session → login');
            window.history.replaceState({}, '', '/login');
            setAppState(prev => ({
              ...prev,
              currentView: 'login',
              isAuthenticated: false,
              loading: false,
            }));
          }
          return;
        }
        // ── END native override ───────────────────────────────────────────────

        // ─────────────────────────────────────────────────────────────────
        // Resolve URL → AppView via the canonical registry.
        // The registry returns `notFound: true` for unknown paths so we
        // can route to the 404 page instead of silently falling back to
        // the marketing homepage.
        // ─────────────────────────────────────────────────────────────────
        let initialView: AppView = resolved.view;

        // Cashfree may land on `/` with payment flags — force payment-success view.
        if (isPaymentFlowInProgress() && (path === '/' || path === '/payment-success')) {
          initialView = 'payment-success';
        }

        // Legacy /optimize → /agent redirect must flip the URL bar too.
        if (resolved.redirectTo && window.location.pathname !== resolved.redirectTo) {
          window.history.replaceState({}, '', resolved.redirectTo);
        }

        // Marketing /business has a special server-side prerender exception.
        if (path === '/business') {
          if (!isPrerenderBuild()) {
            navigateToB2BApp();
            return;
          }
        }

        // /auth/confirm sub-paths (e.g. with hash fragments) should still
        // render the auth-confirm view.
        if (path.includes('/auth/confirm') && initialView === 'not-found') {
          initialView = 'auth-confirm';
        }

        // If user just authenticated and there's a pending redirect, honor it
        // Do not redirect away from /signup/consent (privacy consent step)
        if (path === '/signup/consent') {
          // Require session for consent page; if no session, send to signup
          if (!session) {
            initialView = 'signup';
            window.history.replaceState({}, '', '/signup');
          }
        } else if (
          session
          && (path === '/login' || path === '/signup' || (initialView === 'home' && path === '/'))
          && !isPaymentReturnPath(path, searchParams.toString())
          && !isPaymentFlowInProgress()
          && path !== '/payment-success'
        ) {
          const redirectPath = sessionStorage.getItem('redirect_after_login');
          
          if (redirectPath) {
            const targetPath = resolvePostAuthRedirect(redirectPath);
            appLogger.debug('APP STATE: Redirecting to destination:', targetPath);
            sessionStorage.removeItem('redirect_after_login');
            const redirectResolved = resolveAppViewFromPath(targetPath);
            commitPostAuthLanding(targetPath, redirectResolved.view, 'init-redirect_after_login');
            return;
          }
          
          // Default: redirect to agent (landing page) if authenticated and on public page (not signup/consent)
          if (path === '/' || path === '/login' || path === '/signup') {
            appLogger.debug('APP STATE: Redirecting to agent');
            commitPostAuthLanding(POST_AUTH_LANDING_PATH, POST_AUTH_LANDING_VIEW, 'init-public-auth');
            return;
          }
        }

        // If on /signup/consent without session, path already handled above

        // No longer need to special-case /optimize for auth-loading flicker —
        // the route now redirects to /agent immediately, and the agent page
        // has its own session-aware loader. Keep this branch for any future
        // routes that need it.
        const stayLoadingForAuth = false;

        setAppState(prev => ({
          ...prev,
          currentView: postAuthGuard?.view ?? initialView,
          isAuthenticated: !!session,
          loading: stayLoadingForAuth ? true : false
        }));

      } catch (error) {
        appLogger.error('Error initializing app state:', error);
        setAppState(prev => ({
          ...prev,
          error: 'Failed to initialize application',
          loading: false
        }));
      }
    };

    // Initialize immediately without waiting for auth
    initializeAppState();
    
    // Listen for browser back/forward button
    const handlePopState = () => {
      appLogger.debug('AppStateManager: Browser back/forward - re-initializing');
      const postAuthGuard = enforcePostAuthRouteGuard(!!session);
      if (postAuthGuard) {
        setAppState(prev => ({
          ...prev,
          currentView: postAuthGuard.view,
          isAuthenticated: true,
          loading: false,
        }));
      }
      initializeAppState();
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  // Depend on stable primitives only: re-init when the user changes or auth
  // loading resolves, NOT when the token is silently refreshed (which creates
  // a new session object every tab-focus but keeps the same user ID).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id ?? null, authLoading]);

  // Product tour: immediately sync AppView when tour changes the URL.
  useEffect(() => {
    const onTourNav = (e: Event) => {
      const { path } = (e as CustomEvent<{ path: string }>).detail;
      const resolved = resolveAppViewFromPath(path);
      if (!resolved.notFound) {
        setAppState(prev => ({
          ...prev,
          currentView: resolved.view,
          loading: false,
        }));
      }
    };
    window.addEventListener(TOUR_NAVIGATE_EVENT, onTourNav);
    return () => window.removeEventListener(TOUR_NAVIGATE_EVENT, onTourNav);
  }, []);

    // SECURITY: Continuously monitor session state for protected routes
  useEffect(() => {
    const currentPath = window.location.pathname;
    const isOnProtectedRoute = isProtectedRoute(currentPath);
    
    // CRITICAL FIX: Check if we're in OAuth grace period
    const oauthJustCompleted = sessionStorage.getItem('oauth_just_completed');
    const oauthCompletedTime = oauthJustCompleted ? parseInt(oauthJustCompleted) : 0;
    const timeSinceOAuth = Date.now() - oauthCompletedTime;
    const isInOAuthGracePeriod = oauthJustCompleted && timeSinceOAuth < 5000;
    
    // CRITICAL FIX: Check if tab session marker exists
    const tabSessionMarker = sessionStorage.getItem('tab_session_active');
    // Skip redirect when user just returned from payment (Cashfree) – session may not be in React state yet
    const paymentInProgress = isPaymentFlowInProgress();
    const onPaymentReturn = isPaymentReturnPath(currentPath, window.location.search);

    // Also check if a payment result page is actively rendered (survives clearPaymentFlowFlags)
    const paymentPageActive = (() => {
      try { return sessionStorage.getItem('ggj_payment_page_active'); } catch { return null; }
    })();

    // If user is on protected route but session is lost (e.g., after sign out)
    // BUT: Skip this check if:
    // 1. OAuth grace period is active
    // 2. Tab session marker exists
    // 3. Auth is still loading
    // 4. Payment just completed (user on payment-success or returning from Cashfree)
    // 5. A payment result page is actively rendered
    if (isOnProtectedRoute && !session && !authLoading && !isInOAuthGracePeriod && !tabSessionMarker && !paymentInProgress && !onPaymentReturn && !paymentPageActive) {
      // Double-check with Supabase storage before redirecting — React state
      // can be stale for a frame after token rotation or tab-switch rehydration.
      supabase.auth.getSession().then(({ data: { session: freshSession } }) => {
        if (freshSession) {
          appLogger.debug('SECURITY: Session recovered from storage — staying on', currentPath);
          sessionStorage.setItem('tab_session_active', Date.now().toString());
          return;
        }
        appLogger.debug('SECURITY: Session confirmed lost on protected route:', currentPath);
        appLogger.debug('SECURITY: Redirecting to /login');

        const redirectTarget = currentPath === '/payment-success' ? '/dashboard' : currentPath;
        sessionStorage.setItem('redirect_after_login', redirectTarget);
        window.location.href = '/login';
      });
    }
  // Depend on stable primitives: !!session (boolean) changes only on actual
  // sign-in/out, not on silent token refreshes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!session, authLoading]);

  // Check if user needs onboarding after authentication
  useEffect(() => {
    if (!session || !user) return;
    if (profileLoading || preferencesLoading) return;
    if (!preferences) return;

    const needsOnboardingFlow = !isOnboardingComplete(userProfile, preferences);
    setNeedsOnboarding(needsOnboardingFlow);

    if (needsOnboardingFlow) {
      logEvent({
        level: 'info',
        action: 'onboarding_required',
        details: { userId: (user as { id: string }).id },
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences, preferencesLoading, profileLoading, session?.user?.id ?? null, userProfile]);

  // Sprint 2 / TASK 0-2: redirect to /onboarding when the user must complete
  // the profile wizard (new signups have no row yet; row defaults to incomplete).
  //
  // Rules:
  //   * Redirect when there is no user_profiles row OR onboarding_completed is false.
  //   * Skips when already on /onboarding (prevents redirect loops).
  //   * Skips on auth/payment/marketing pages (not "protected" routes).
  useEffect(() => {
    if (!session)         return;
    if (authLoading)      return;
    if (profileLoading)   return;
    if (preferencesLoading) return;
    if (isOnboardingComplete(userProfile, preferences)) return;

    const path = window.location.pathname;
    if (path === '/onboarding') return; // already there
    if (path === '/payment-success' || path === '/checkout') return; // never hijack payment flow

    // Onboarding only intercepts authenticated pages. Driven by the
    // canonical registry so adding a new authenticated route automatically
    // adds it to the onboarding gate (the previous inline list was missing
    // /account-settings, /my-activities, /admin/jobs and others).
    if (!AUTH_PATH_PREFIXES.some(r => path === r || path.startsWith(`${r}/`) || path.startsWith(r))) {
      return;
    }

    appLogger.debug('ONBOARDING: wizard required → /onboarding', {
      hasProfile: Boolean(userProfile),
      onboardingCompleted: userProfile?.onboardingCompleted ?? null,
    });
    window.history.replaceState({}, '', '/onboarding');
    setAppState(prev => ({
      ...prev,
      currentView: 'onboarding',
      isAuthenticated: true,
      loading: false,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    !!session,
    authLoading,
    profileLoading,
    preferencesLoading,
    userProfile?.onboardingCompleted ?? null,
    userProfile?.id ?? null,
    userProfile?.displayName ?? null,
    userProfile?.currentRole ?? null,
    userProfile?.experienceYears ?? null,
    preferences?.onboarding_complete ?? null,
    appState.currentView,            // re-check on every view change (popstate, manual URL)
  ]);

  // Handle navigation
  const handleNavigate = (view: AppView) => {
    if (view === 'business') {
      navigateToB2BApp();
      return;
    }

    // SECURITY: Resolve the destination URL via the registry first so we
    // know whether the target view requires a session. Using the registry
    // (instead of an inline `protectedViews` list) prevents future drift
    // — adding a new authenticated route in routeRegistry.ts auto-protects
    // it everywhere.
    let effectiveView = view;
    if (session && view === 'home') {
      effectiveView = POST_AUTH_LANDING_VIEW;
    }
    const targetPath = appViewToPath(effectiveView, window.location.pathname);
    const targetIsProtected = isProtectedRoute(targetPath);

    if (targetIsProtected && !session) {
      // React session state can be stale right after payment-return — verify
      // against Supabase storage before bouncing to /login.
      supabase.auth.getSession().then(({ data: { session: freshSession } }) => {
        if (freshSession) {
          appLogger.debug('SECURITY: Fresh session - allowing navigation to', effectiveView);
          setAppState(prev => ({ ...prev, currentView: effectiveView, isAuthenticated: true }));
          if (window.location.pathname !== targetPath) {
            window.history.pushState({}, '', targetPath);
          }
          return;
        }
        appLogger.debug('🔒 SECURITY: Blocked navigation to protected view:', effectiveView);
        // Remember where the user was trying to go so login can bounce them
        // back, but never round-trip back to /payment-success (which would
        // re-trigger payment processing).
        const redirectTarget = effectiveView === 'payment-success' ? '/dashboard' : targetPath;
        sessionStorage.setItem('redirect_after_login', redirectTarget);
        setAppState(prev => ({ ...prev, currentView: 'login', isAuthenticated: false }));
        window.history.pushState({}, '', '/login');
      });
      return;
    }

    setAppState(prev => ({ ...prev, currentView: effectiveView }));
    if (window.location.pathname !== targetPath) {
      window.history.pushState({}, '', targetPath);
    }
  };

  // Handle authentication success
  const handleAuthSuccess = () => {
    // CRITICAL: Don't trigger auth success during password reset flow
    const urlParams = new URLSearchParams(window.location.search);
    const type = urlParams.get('type');
    const tokenHash = urlParams.get('token_hash');
    const mandatoryReset = sessionStorage.getItem('mandatory_password_reset');
    const resetInProgress = sessionStorage.getItem('password_reset_in_progress');
    
    if (type === 'recovery' || tokenHash || mandatoryReset === 'true' || resetInProgress === 'true') {
      appLogger.debug('SECURITY: Blocking auth success - password reset flow active');
      return;
    }
    
    // SECURITY: Check for mandatory password reset
    const currentPath = window.location.pathname;
    if (currentPath === '/reset-password' || currentPath === '/auth/confirm') {
      appLogger.debug('SECURITY: On reset page - not triggering auth success');
      return; // Don't trigger normal auth success flow for password reset
    }
    
    appLogger.debug('AUTH SUCCESS: User authenticated, updating state');
    collapseSidebarForPostLogin();

    const committed = commitPostAuthLanding(
      POST_AUTH_LANDING_PATH,
      POST_AUTH_LANDING_VIEW,
      'handleAuthSuccess',
    );
    if (!committed) {
      return;
    }

    logEvent({
      level: 'info',
      action: 'user_authenticated',
      details: { timestamp: new Date().toISOString() }
    });
  };

  // Handle sign out
  const handleSignOut = async () => {
    try {
      postAuthLandingRef.current = null;
      clearUserLocalData();
      await signOut();

      // On native (Capacitor/Android) there is no marketing homepage — go to login.
      // On web, go to the marketing home page as before.
      const targetView: AppView = isNativePlatform() ? 'login' : 'home';
      const targetPath = isNativePlatform() ? '/login' : '/';

      setAppState(prev => ({
        ...prev,
        isAuthenticated: false,
        currentView: targetView,
      }));
      
      window.history.pushState({}, '', targetPath);
      
    } catch (error) {
      appLogger.error('Error signing out:', error);
    }
  };

  return {
    appState,
    handleNavigate,
    handleAuthSuccess,
    handleSignOut,
    session,
    user,
    consentState,
    needsOnboarding
  };
};
